/**
 * useTournamentGames — loads a tournament's games for Step 3 (Review).
 *
 * Reads the shared tournament games (with their matchedPlayers) via the
 * existing tournament-games service. Fetched fresh whenever the tournament id
 * changes; the itinerary lens is applied client-side over this result (see
 * itinerary.ts) so the same read backs every player selection.
 */

import { useEffect, useRef, useState } from "react";
import { getTournamentGames } from "../../../../../services/tournament-games";
import type { TournamentGameDto } from "../../../../../types/tournament-games";
import { getSafeErrorMessage } from "../../../../../utils/errorHandler";

export type TournamentGamesState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "error"; message: string }
  | {
      status: "ready";
      games: TournamentGameDto[];
      title: string;
      /** Team ids the current user follows that appear in this tournament. */
      followedTeamIds: string[];
      /** Raw names the current user follows (no org team) that appear in this tournament. */
      followedTeamNames: string[];
    };

export function useTournamentGames(
  calendarEventId: string | undefined,
  /** Bump to force a refetch (e.g. after associating a team). */
  reloadToken = 0,
): TournamentGamesState {
  const [state, setState] = useState<TournamentGamesState>(
    calendarEventId ? { status: "loading" } : { status: "idle" },
  );
  const activeRef = useRef(true);

  useEffect(() => {
    activeRef.current = true;
    if (!calendarEventId) {
      setState({ status: "idle" });
      return () => {
        activeRef.current = false;
      };
    }

    setState({ status: "loading" });
    const load = async () => {
      try {
        const response = await getTournamentGames(calendarEventId);
        if (!activeRef.current) return;
        setState({
          status: "ready",
          games: response.games,
          title: response.title,
          followedTeamIds: response.followedTeamIds ?? [],
          followedTeamNames: response.followedTeamNames ?? [],
        });
      } catch (err) {
        if (!activeRef.current) return;
        setState({
          status: "error",
          message: getSafeErrorMessage(
            err,
            "We could not load this tournament's games. Please try again.",
          ),
        });
      }
    };

    void load();
    return () => {
      activeRef.current = false;
    };
  }, [calendarEventId, reloadToken]);

  return state;
}
